import React, { useEffect, useState } from 'react'
import fetch from 'isomorphic-fetch'

// Apollo Client Imports
import { ApolloClient } from 'apollo-client'
import { setContext } from 'apollo-link-context'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { HttpLink } from 'apollo-link-http'
import { split } from 'apollo-link'
import { WebSocketLink } from 'apollo-link-ws'
import { getMainDefinition } from 'apollo-utilities'
import { ApolloProvider as Provider } from '@apollo/react-hooks'
import {
   isClient,
   get_env,
   checkIfTokenExpired,
   getCookies,
   isSessionIdValid,
   removeSessionId,
   sessionId,
   isKiosk,
   generateSession,
} from '../utils'
import { isUndefined } from 'lodash'
import { getAxios } from './axios'

const authLink = setContext(async (_, { headers }) => {
   let nextAuthToken = getCookies()['next-auth-token']
   const [digitalMenuScreenCodeTemp, digitalMenuScreenCode] = [
      localStorage.getItem('digital-menu-screen-code-temp'),
      localStorage.getItem('digital-menu-screen-code'),
   ]

   // Remove Tokens and reload the page if token is expired
   if (
      checkIfTokenExpired(
         sessionStorage.getItem('kioskGuestSessionToken') ||
            sessionStorage.getItem('guestSessionToken')
      )
   ) {
      sessionStorage.removeItem('kioskGuestSessionToken')
      sessionStorage.removeItem('guestSessionToken')
      window.location.reload()
   }

   const sessionId = sessionStorage.getItem('sessionId')

   if (sessionId && !isSessionIdValid()) {
      removeSessionId()
   }

   return {
      headers: {
         ...headers,
         'app-version': process.env.NEXT_PUBLIC_API_SERVER_VERSION,
         ...(nextAuthToken && {
            'next-auth-token': nextAuthToken,
         }),
         ...(sessionStorage &&
            (sessionStorage.getItem('kioskGuestSessionToken') ||
               sessionStorage.getItem('guestSessionToken')) && {
               'Guest-Session-Token':
                  sessionStorage.getItem('kioskGuestSessionToken') ||
                  sessionStorage.getItem('guestSessionToken'),
            }),
         ...((digitalMenuScreenCodeTemp || digitalMenuScreenCode) && {
            'digital-menu-screen-code':
               digitalMenuScreenCodeTemp || digitalMenuScreenCode,
         }),
         ...(sessionStorage &&
            sessionId && {
               'session-id': sessionId,
            }),
      },
   }
})

export async function getGuestSessionToken() {
   let url = `${
      new URL(get_env('GRAPHQL_HTTP_URL')).origin
   }/datahub/api/rest/generateGuestSessionToken`

   const response = await getAxios()({
      url,
      method: 'POST',
   })
   const { guestSessionToken: _guestSessionToken, success } =
      response.data?.generateGuestSessionToken || {}
   // Store Guest Session Token into Session Storage so that this can be further used in authorization
   if (success && _guestSessionToken) {
      sessionStorage.setItem('guestSessionToken', _guestSessionToken)
      return _guestSessionToken
   }
}

function getWsLink() {
   return process.browser
      ? new WebSocketLink({
           uri: isClient && get_env('GRAPHQL_WS_URL'),
           options: {
              lazy: true,
              reconnect: true,
              connectionParams: async () => {
                 let nextAuthToken = getCookies()['next-auth-token']
                 let guestSessionToken = ''

                 if (
                    sessionStorage.getItem('kioskGuestSessionToken') ||
                    sessionStorage.getItem('guestSessionToken')
                 ) {
                    guestSessionToken =
                       sessionStorage.getItem('kioskGuestSessionToken') ||
                       sessionStorage.getItem('guestSessionToken')
                 }

                 if (guestSessionToken.length <= 0) {
                    try {
                       guestSessionToken = await getGuestSessionToken()
                    } catch (err) {
                       console.error(
                          '==> Error in generating guest session token',
                          err
                       )
                    }
                 }
                 const [digitalMenuScreenCodeTemp, digitalMenuScreenCode] = [
                    localStorage.getItem('digital-menu-screen-code-temp'),
                    localStorage.getItem('digital-menu-screen-code'),
                 ]

                 // Remove Tokens and reload the page if token is expired
                 if (checkIfTokenExpired(guestSessionToken)) {
                    sessionStorage.removeItem('kioskGuestSessionToken')
                    sessionStorage.removeItem('guestSessionToken')
                    window.location.reload()
                 }

                 return {
                    headers: {
                       'app-version':
                          process.env.NEXT_PUBLIC_API_SERVER_VERSION,
                       ...(guestSessionToken && {
                          'Guest-Session-Token': guestSessionToken,
                       }),
                       ...(nextAuthToken && {
                          'next-auth-token': nextAuthToken,
                       }),
                       ...((digitalMenuScreenCodeTemp ||
                          digitalMenuScreenCode) && {
                          'digital-menu-screen-code':
                             digitalMenuScreenCodeTemp || digitalMenuScreenCode,
                       }),
                    },
                 }
              },
           },
        })
      : null
}

const httpLink = new HttpLink({
   uri: isClient && get_env('GRAPHQL_HTTP_URL'),
})

function getLink(wsLink) {
   return process.browser
      ? split(
           ({ query }) => {
              const definition = getMainDefinition(query)
              return (
                 definition.kind === 'OperationDefinition' &&
                 definition.operation === 'subscription'
              )
           },
           wsLink || getWsLink(),
           authLink.concat(httpLink)
        )
      : httpLink
}

function getClient() {
   const wsLink = getWsLink()
   const client = new ApolloClient({
      link: getLink(wsLink),
      fetch,
      cache: new InMemoryCache(),
   })

   return { client, wsLink }
}

export const ApolloProvider = ({ children }) => {
   const [{ client, wsLink }, setClient] = useState(getClient())
   const [generatingGuestSessionToken, setGeneratingGuestSessionToken] =
      useState(true)

   const restartWebsocketConnection = (isAuthenticated, _wsLink) => {
      if (
         isAuthenticated !== undefined &&
         client.isAuthenticated === isAuthenticated
      )
         return
      _wsLink.subscriptionClient.close()
      const wsLink = getWsLink()
      const newClient = getClient(getLink(wsLink))
      if (!isUndefined(isAuthenticated)) {
         newClient.client.isAuthenticated = isAuthenticated
      } else {
         newClient.client.isAuthenticated = client.isAuthenticated
      }
      setClient(newClient)
   }

   useEffect(() => {
      if (!client || !wsLink) return
      client.restartWebsocketConnection = isAuthenticated => {
         restartWebsocketConnection(isAuthenticated, wsLink)
      }
   }, [client, wsLink])

   useEffect(() => {
      const kioskGuestSessionToken = sessionStorage.getItem(
         'kioskGuestSessionToken'
      )
      const guestSessionToken = sessionStorage.getItem('guestSessionToken')
      if (!kioskGuestSessionToken && !guestSessionToken) {
         getGuestSessionToken().then(async gst => {
            setGeneratingGuestSessionToken(false)
         })
      } else {
         setGeneratingGuestSessionToken(false)
      }
   }, [])

   useEffect(() => {
      /* 
      if session-id already existing when user visiting the page
      
         check weather sessionId is valid or not 
         
            if sessionId is valid then do nothing

            other wise remove already existing sessionId and generate new sessionId

      create new session if not kiosk
     */
      ;(async function () {
         if (sessionId && !isSessionIdValid) {
            removeSessionId()
            await generateSession()
         } else {
            if (!isKiosk) {
               await generateSession()
            }
         }
      })()
   }, [])

   if (generatingGuestSessionToken) {
      return null
   }

   return <Provider client={client}>{children}</Provider>
}
